import { useEffect, useRef } from "react";

// Hand-rolled canvas confetti for the done-screen celebration (M6) — no new
// deps. Mounts a fixed full-viewport canvas, runs one burst, then calls
// onDone so the caller can unmount it. Skips the animation loop entirely
// under prefers-reduced-motion (styles.css's blanket reduced-motion rule
// only shortens CSS animations/transitions, not canvas RAF loops, so this
// needs its own check) — onDone still fires immediately so callers don't
// need a separate reduced-motion branch.

const COLORS = ["#34d399", "#60a5fa", "#fbbf24", "#f87171", "#fafafa"];
const DURATION_MS = 1700;

type Particle = {
  x: number; y: number; vx: number; vy: number; rot: number; vr: number; size: number; color: string; round: boolean;
};

export function ConfettiBurst({ count = 70, onDone }: { count?: number; onDone?: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (reduced || !canvas || !ctx) {
      onDone?.();
      return;
    }

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    const originX = w / 2;
    const particles: Particle[] = Array.from({ length: count }, () => {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI * 0.9);
      const speed = 5 + Math.random() * 7;
      return {
        x: originX + (Math.random() - 0.5) * 60,
        y: h * 0.32,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.4,
        size: 5 + Math.random() * 5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        round: Math.random() > 0.5,
      };
    });

    let raf = 0;
    const gravity = 0.22;
    const drag = 0.992;
    const start = performance.now();

    function frame(now: number) {
      const elapsed = now - start;
      ctx!.clearRect(0, 0, w, h);
      const fade = Math.max(0, 1 - elapsed / DURATION_MS);
      for (const p of particles) {
        p.vx *= drag;
        p.vy = p.vy * drag + gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        ctx!.save();
        ctx!.globalAlpha = fade;
        ctx!.translate(p.x, p.y);
        ctx!.rotate(p.rot);
        ctx!.fillStyle = p.color;
        if (p.round) {
          ctx!.beginPath();
          ctx!.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx!.fill();
        } else {
          ctx!.fillRect(-p.size / 2, -p.size / 3, p.size, p.size * 0.66);
        }
        ctx!.restore();
      }
      if (elapsed < DURATION_MS) {
        raf = requestAnimationFrame(frame);
      } else {
        onDone?.();
      }
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <canvas ref={canvasRef} className="confetti-canvas" data-testid="confetti" aria-hidden="true" />;
}
